/* stock-by-location.js — KNS multi-location inventory display.
 *
 * Web Component that lives inside the product page's main section.
 * On connect and on every `on:variant:change` event, it fetches per-
 * location stock from the kns-stock-proxy Cloudflare Worker (via Shopify
 * App Proxy `/apps/kns/stock`) and updates the location rows and the
 * delivery-unavailable warning banner.
 *
 * Failure mode: any network/parse error leaves the last successful
 * render in place. The page is never broken; stale > wrong.
 */
(function () {
  "use strict";

  const VARIANT_CHANGE_EVENT = "on:variant:change";
  const STATE_EVENT = "kns:stock-state";
  const DEBOUNCE_MS = 150;

  class StockByLocation extends HTMLElement {
    constructor() {
      super();
      this._lastVariantId = null;
      this._timer = null;
      this._lastState = null;
    }

    connectedCallback() {
      this._proxyUrl = this.dataset.proxyUrl || "/apps/kns/stock";
      this._loc = this.dataset.loc || "";
      this._labels = {
        available: this.dataset.labelAvailable || "Available",
        in_stock: this.dataset.labelInStock || "In stock at {{ location }}",
        low: this.dataset.labelLow || "Low stock at {{ location }}",
        out: this.dataset.labelOut || "Out of stock at {{ location }}",
      };

      this._sectionRoot = this.closest(".js-product, .product, [data-section-type=\"main-product\"]") || this;

      // Hook variant changes both via the standard custom event and as a
      // belt-and-braces fallback when a hidden <input name="id"> changes.
      this._sectionRoot.addEventListener(VARIANT_CHANGE_EVENT, this._onVariantChange.bind(this));
      const idInput = this._sectionRoot.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (idInput) {
        idInput.addEventListener("change", () => this._scheduleFetch(Number(idInput.value)));
      }

      const initial = Number(this.dataset.currentVariantId);
      if (initial) this._scheduleFetch(initial, /*immediate*/ true);
    }

    _onVariantChange(ev) {
      const variant = ev && ev.detail && ev.detail.variant;
      if (!variant || !variant.id) return;
      this._scheduleFetch(Number(variant.id));
    }

    _scheduleFetch(variantId, immediate) {
      if (!variantId || variantId === this._lastVariantId && !immediate) return;
      clearTimeout(this._timer);
      const delay = immediate ? 0 : DEBOUNCE_MS;
      this._timer = setTimeout(() => this._fetch(variantId), delay);
    }

    async _fetch(variantId) {
      this._lastVariantId = variantId;
      const url = `${this._proxyUrl}?variant_id=${encodeURIComponent(variantId)}&loc=${encodeURIComponent(this._loc)}`;
      try {
        const res = await fetch(url, { credentials: "omit", headers: { accept: "application/json" } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (variantId !== this._lastVariantId) return; // stale response
        this._render(data);
      } catch (err) {
        console.warn("[stock-by-location] fetch failed", err);
        // Keep the previous render — better stale than broken.
      }
    }

    _render(data) {
      const locations = (data && Array.isArray(data.locations)) ? data.locations : [];
      let anyAvailable = false;
      let deliveryAvailable = false;

      const rowsByLocation = new Map();
      this.querySelectorAll(".kns-stock__card").forEach((row) => {
        rowsByLocation.set(row.dataset.locationId, row);
      });

      for (const loc of locations) {
        const row = rowsByLocation.get(String(loc.id));
        if (!row) continue;
        row.dataset.tier = loc.tier;
        // too_many is an internal tier; it renders identically to in_stock for customers.
        const labelTemplate = this._labels[loc.tier === "too_many" ? "in_stock" : loc.tier] || "";
        const labelNode = row.querySelector("[data-label]");
        const baseLocationName = (row.dataset.locationName || labelNode?.dataset.locationName || labelNode?.textContent?.trim() || "");
        if (labelNode && !labelNode.dataset.locationName) labelNode.dataset.locationName = baseLocationName;
        const labelText = labelTemplate.replace("{{ location }}", baseLocationName);
        if (labelNode) labelNode.textContent = labelText;

        // Tier "available" = untracked / non-physical variant: no quantity,
        // always purchasable, so it counts as available everywhere.
        if (loc.tier === "available" || loc.available > 0) {
          anyAvailable = true;
          if (loc.role === "delivery") deliveryAvailable = true;
        }
      }

      const warning = this.querySelector("[data-warning]");
      if (warning) {
        const show = anyAvailable && !deliveryAvailable;
        warning.hidden = !show;
      }

      this._lastState = { any_available: anyAvailable, delivery_available: deliveryAvailable };
      // Note: we deliberately do NOT touch the Add-to-Cart button's disabled state.
      // Shopify's native `variant.available` is the source of truth for that.
      // Our component only adds the delivery-unavailable warning banner.
      this._dispatchState();
    }

    _dispatchState() {
      if (!this._lastState) return;
      this.dispatchEvent(new CustomEvent(STATE_EVENT, {
        bubbles: true,
        detail: this._lastState,
      }));
    }
  }

  if (!customElements.get("stock-by-location")) {
    customElements.define("stock-by-location", StockByLocation);
  }
})();
